import React from "react";
import {
  FaHeart,
  FaRegHeart,
  FaSearch,
  FaShareAlt,
  FaTimes,
} from "react-icons/fa";

export default function TechniquesFiltersCard({
  ui,
  dynamicOptions,
  debouncedSearch,
  setDebouncedSearch,
  onChange,
  onReset,
  onShare,
  shareCopied,
  loading,
  resultCount,
  totalCount,
  favoriteCount,
  hasUser,
}) {
  const fieldClass =
    "w-full rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-900/30";

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Filtres techniques
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Retrouver rapidement une technique
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Les familles sont construites dynamiquement à partir des
              techniques enregistrées dans le club.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
            <span className="text-slate-500 dark:text-slate-400">
              Résultats :
            </span>{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {resultCount}
            </span>
            <span className="text-slate-500 dark:text-slate-400">
              {" "}
              / {totalCount}
            </span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Recherche
            </label>

            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={debouncedSearch}
                onChange={(event) => setDebouncedSearch(event.target.value)}
                disabled={loading}
                placeholder="Nom, traduction, famille, ceinture..."
                className="w-full rounded-2xl border border-slate-300 bg-white py-2.5 pl-11 pr-3 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-900/30"
              />
            </div>
          </div>

          <div className="lg:col-span-3">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Ceinture
            </label>

            <select
              value={ui.grade}
              onChange={(event) => onChange({ grade: event.target.value })}
              disabled={loading}
              className={fieldClass}
            >
              {dynamicOptions.grades.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-4">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Famille
            </label>

            <select
              value={ui.family}
              onChange={(event) => onChange({ family: event.target.value })}
              disabled={loading}
              className={fieldClass}
            >
              {dynamicOptions.families.map((family) => (
                <option key={family} value={family}>
                  {family}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-4">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Fiche UV2
            </label>

            <select
              value={ui.uv2}
              onChange={(event) => onChange({ uv2: event.target.value })}
              disabled={loading}
              className={fieldClass}
            >
              {dynamicOptions.uv2.map((uv) => (
                <option key={uv} value={uv}>
                  {uv === "Toutes les fiches" ? "Toutes les fiches UV2" : uv}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-4">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Tri
            </label>

            <select
              value={ui.sort}
              onChange={(event) => onChange({ sort: event.target.value })}
              disabled={loading}
              className={fieldClass}
            >
              <option value="beltFamily">Ceinture ▸ Famille ▸ Nom</option>
              <option value="alpha">Tri alphabétique</option>
              <option value="grade">Tri par ceinture</option>
              <option value="family">Tri par famille</option>
            </select>
          </div>

          <div className="lg:col-span-4">
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Actions
            </label>

            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => onChange({ showFav: !ui.showFav })}
                disabled={loading || !hasUser}
                title={
                  hasUser
                    ? "Afficher uniquement mes favoris"
                    : "Connectez-vous pour utiliser les favoris"
                }
                className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl px-3 text-sm font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  ui.showFav
                    ? "bg-red-50 text-red-600 ring-red-200 dark:bg-red-900/20 dark:text-red-200 dark:ring-red-800/60"
                    : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
                }`}
              >
                {ui.showFav ? <FaHeart /> : <FaRegHeart />}
                <span className="hidden sm:inline">{favoriteCount}</span>
              </button>

              <button
                type="button"
                onClick={onShare}
                disabled={loading}
                className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl px-3 text-sm font-bold ring-1 transition disabled:opacity-60 ${
                  shareCopied
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
                    : "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
                }`}
              >
                <FaShareAlt />
              </button>

              <button
                type="button"
                onClick={onReset}
                disabled={loading}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-slate-50 px-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100 disabled:opacity-60 dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}